import type { ReactNode } from "react";
import { OptimizedImage } from "../OptimizedImage";

interface ContainerProps {
  children: ReactNode;
  className?: string;
  variant?: "default" | "hero-overlay" | "category-card" | "simple-box";
  background?: string;
  padding?: "none" | "sm" | "md" | "lg" | "xl";
  margin?: "none" | "sm" | "md" | "lg" | "xl";
  shadow?: "none" | "sm" | "md" | "lg" | "xl";
  rounded?: "none" | "sm" | "md" | "lg" | "xl" | "full";
  overlayEffect?: boolean;
  onClick?: () => void;
}

export function Container({
  children,
  className = "",
  variant = "default",
  background,
  padding = "md",
  margin = "none",
  shadow = "md",
  rounded = "lg",
  overlayEffect = false,
  onClick,
}: ContainerProps) {
  // Configuración de variantes
  const variantStyles = {
    default: "bg-white",
    "hero-overlay": "bg-white/90 backdrop-blur-sm",
    "category-card": "relative overflow-hidden",
    "simple-box": "bg-gray-50 border border-gray-200",
  };

  // Configuración de padding
  const paddingStyles = {
    none: "",
    sm: "p-2",
    md: "p-4",
    lg: "p-6",
    xl: "p-8",
  };

  // Configuración de margin
  const marginStyles = {
    none: "",
    sm: "m-2",
    md: "m-4",
    lg: "m-6",
    xl: "m-8",
  };

  // Configuración de shadow
  const shadowStyles = {
    none: "",
    sm: "shadow-sm",
    md: "shadow-md",
    lg: "shadow-lg",
    xl: "shadow-xl",
  };

  // Configuración de border radius
  const roundedStyles = {
    none: "",
    sm: "rounded",
    md: "rounded-md",
    lg: "rounded-lg",
    xl: "rounded-xl",
    full: "rounded-full",
  };

  // Construir clases base
  const baseClasses = [
    variantStyles[variant],
    paddingStyles[padding],
    marginStyles[margin],
    shadowStyles[shadow],
    roundedStyles[rounded],
    overlayEffect && "backdrop-blur-sm",
    onClick && "cursor-pointer hover:shadow-lg transition-shadow duration-200",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // Renderizar con estructura de capas para category-card
  if (variant === "category-card") {
    return (
      <div className={baseClasses} onClick={onClick}>
        {/* Capa 1: Imagen de fondo optimizada */}
        {background && (
          <div className="absolute inset-0">
            <OptimizedImage
              src={background}
              alt="Background"
              className="w-full h-full object-cover"
              width={800}
              height={400}
              quality={85}
              format="webp"
              loading="lazy"
            />
          </div>
        )}

        {/* Capa 2: Overlay oscuro semi-transparente */}
        <div className="absolute inset-0 bg-black/30"></div>

        {/* Capa 3: Contenido */}
        <div className="relative z-10 h-full flex flex-col justify-end">
          {children}
        </div>
      </div>
    );
  }

  // Estilos inline para background personalizado (para otras variantes)
  const inlineStyles: React.CSSProperties = {};
  if (
    background &&
    !background.startsWith("http") &&
    !background.startsWith("/")
  ) {
    // Solo aplicar estilos inline para colores, no para URLs
    inlineStyles.backgroundColor = background;
  }

  return (
    <div className={baseClasses} style={inlineStyles} onClick={onClick}>
      {children}
    </div>
  );
}
